// src/pages/agents/AgentToolsTab.js
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper,
  Button, IconButton, CircularProgress, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete, TextField
} from '@mui/material';
import {
  Edit as EditIcon,
  Code as CodeIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useOutletContext, useNavigate } from 'react-router-dom';
import InfoBox from '../../components/common/InfoBox';
import { agentService } from '../../api/agentService';
import { toolService } from '../../api/toolService';
import { toast } from 'react-toastify';

const AgentToolsTab = () => {
  const { agent } = useOutletContext();
  const navigate = useNavigate();

  const [assignedTools, setAssignedTools] = useState([]); // Stores full tool objects that are assigned
  const [allSystemTools, setAllSystemTools] = useState([]); // Stores all available full tool objects
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isAddToolDialogOpen, setIsAddToolDialogOpen] = useState(false);
  const [selectedToolsInDialog, setSelectedToolsInDialog] = useState([]); // Stores full tool objects selected in dialog
  const [savingTools, setSavingTools] = useState(false);

  const fetchAgentAndSystemTools = async () => {
    setLoading(true);
    setError(null);
    try {
      // resAssigned: Expected Array<string> of tool slugs assigned to the agent.
      // resAll: Expected Array<ToolObject>, where each object has a 'slug' field.
      const [resAssigned, resAll] = await Promise.all([
        agentService.getAgentTools(agent.id),
        toolService.getAllTools()
      ]);

      // Optional: Keep these logs for initial verification if needed, then remove.
      // console.log("AgentToolsTab: Slugs from agentService.getAgentTools (resAssigned):", JSON.stringify(resAssigned));
      // console.log("AgentToolsTab: Full tools from toolService.getAllTools (resAll):", JSON.stringify(resAll));

      let fetchError = null;
      if (resAssigned.isError) {
        fetchError = `Failed to load assigned tool slugs: ${resAssigned.message}`;
      }
      if (resAll.isError) {
        fetchError = `${fetchError || ''} Failed to load all system tools: ${resAll.message}`;
      }
      // Basic check if resAll has the expected structure (contains 'slug')
      if (!resAll.isError && Array.isArray(resAll) && resAll.length > 0 && typeof resAll[0].slug === 'undefined') {
          fetchError = `${fetchError || ''} System tools are missing the 'slug' field.`;
          console.error("AgentToolsTab: Critical error - Tools fetched from toolService.getAllTools() are missing the 'slug' field, even though backend was updated.");
      }


      if (fetchError) {
        setError(fetchError);
        toast.error(fetchError);
        setAssignedTools([]);
        // Still set allSystemTools so the 'Add Tool' dialog can potentially list them if resAll was successful
        setAllSystemTools(Array.isArray(resAll) && !resAll.isError ? resAll : []);
      } else {
        const assignedSlugs = Array.isArray(resAssigned) ? resAssigned : [];
        const allToolObjects = Array.isArray(resAll) ? resAll : [];

        // Filter allSystemTools to find tool objects whose 'slug' is in the assignedSlugs list
        const currentAssignedToolObjects = allToolObjects.filter(tool =>
          tool.slug && assignedSlugs.includes(tool.slug)
        );
        
        // console.log("AgentToolsTab: Filtered assignedToolObjects (matched by slug):", JSON.stringify(currentAssignedToolObjects));

        setAssignedTools(currentAssignedToolObjects);
        setAllSystemTools(allToolObjects);
      }
    } catch (err) {
        const errMsg = `An unexpected error occurred while fetching tools: ${err.message}`;
        setError(errMsg);
        toast.error(errMsg);
        setAssignedTools([]);
        setAllSystemTools([]);
    } finally {
        setLoading(false);
    }
  };

  useEffect(() => {
    if (agent?.id) {
      fetchAgentAndSystemTools();
    }
  }, [agent?.id]);

  const handleOpenAddToolDialog = () => {
    setSelectedToolsInDialog([]);
    setIsAddToolDialogOpen(true);
  };

  const handleCloseAddToolDialog = () => {
    setIsAddToolDialogOpen(false);
  };

  const handleAddSelectedTools = async () => {
    if (selectedToolsInDialog.length === 0) {
      toast.info("No tools selected to add.");
      return;
    }
    // Ensure all selected tools have a slug
    if (selectedToolsInDialog.some(t => !t.slug)) {
        toast.error("One or more selected tools are invalid (missing slug). Cannot add.");
        return;
    }

    setSavingTools(true);
    const currentAssignedSlugs = assignedTools.map(t => t.slug);
    const newToolSlugsToAdd = selectedToolsInDialog.map(t => t.slug);

    const updatedToolSlugs = Array.from(new Set([...currentAssignedSlugs, ...newToolSlugsToAdd]));
    
    // console.log("AgentToolsTab: Sending these slugs to updateAgentTools:", JSON.stringify(updatedToolSlugs));

    const res = await agentService.updateAgentTools(agent.id, updatedToolSlugs); 
    if (!res.isError) {
      toast.success("Tools updated successfully.");
      // res from updateAgentTools is the new list of slugs for the agent
      const newListOfAssignedSlugs = Array.isArray(res) ? res : [];
      const newAssignedToolObjects = allSystemTools.filter(tool => tool.slug && newListOfAssignedSlugs.includes(tool.slug));
      setAssignedTools(newAssignedToolObjects);
      handleCloseAddToolDialog();
    } else {
      toast.error(`Failed to update tools: ${res.message}`);
    }
    setSavingTools(false);
  };

  const handleRemoveTool = async (toolSlugToRemove) => {
    const toolObjectToRemove = assignedTools.find(t => t.slug === toolSlugToRemove);
    if (!window.confirm(`Are you sure you want to remove the tool "${toolObjectToRemove?.name || toolSlugToRemove}" from this agent?`)) return;

    setSavingTools(true);
    const newSlugs = assignedTools
      .filter(t => t.slug !== toolSlugToRemove)
      .map(t => t.slug);

    const res = await agentService.updateAgentTools(agent.id, newSlugs);
    if (!res.isError) {
      toast.success(`Tool "${toolObjectToRemove?.name || toolSlugToRemove}" removed successfully.`);
      setAssignedTools(prev => prev.filter(t => t.slug !== toolSlugToRemove));
    } else {
      toast.error(`Failed to remove tool: ${res.message}`);
    }
    setSavingTools(false);
  };

  const availableToolsForDialog = useMemo(() => {
    const assignedToolSlugs = new Set(assignedTools.map(t => t.slug));
    // Only list tools that have a slug and are not already assigned
    return allSystemTools.filter(tool => tool.slug && !assignedToolSlugs.has(tool.slug));
  }, [allSystemTools, assignedTools]);

  // Display loading or error states
  if (loading) return <Box display="flex" justifyContent="center" p={3}><CircularProgress /><Typography sx={{ml:1}}>Loading tools...</Typography></Box>;
  
  // If there's a general error and no tools could be determined as assigned (even if allSystemTools might have data)
  if (error && assignedTools.length === 0 && !loading) {
    return <InfoBox type="error" title="Error Loading Tools Information">{error}</InfoBox>;
  }
  // If there was an error but some tools were already loaded/determined, show a toast.
  if (error && !loading) {
      toast.warning(`There was an issue: ${error}. Displayed tools might be incomplete.`, { autoClose: 7000 });
  }


  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Assigned Tools</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAddToolDialog}
          // Disable if still loading, or no tools are available to add, or allSystemTools is empty.
          disabled={loading || availableToolsForDialog.length === 0 || allSystemTools.length === 0}
        >
          Add Tool
        </Button>
      </Box>

      {assignedTools.length === 0 && !loading ? ( // Check !loading again in case of initial load with no tools
        <InfoBox type="info" title="No Tools Assigned">
          This agent currently has no tools assigned. Click "Add Tool" to assign some.
          {error && <Typography color="error" variant="caption" display="block" sx={{mt:1}}>Details: {error}</Typography>}
        </InfoBox>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Slug</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignedTools.map(tool => (
                // tool.id (UUID) is the best React key for uniqueness of the tool object
                <TableRow key={tool.id} hover> 
                  <TableCell>{tool.name}</TableCell>
                  <TableCell>{tool.slug}</TableCell>
                  <TableCell>{tool.tool_type}</TableCell>
                  <TableCell>{tool.enabled ? 'Enabled' : 'Disabled'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit Tool (Not Implemented)">
                      <IconButton onClick={() => toast.info("Tool editing not implemented.")} disabled>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Tool Definition (Not Implemented)">
                      <IconButton onClick={() => toast.info("Tool definition viewing not implemented.")} disabled>
                        <CodeIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove from Agent">
                      <IconButton color="error" onClick={() => handleRemoveTool(tool.slug)} disabled={savingTools}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={isAddToolDialogOpen} onClose={handleCloseAddToolDialog} fullWidth maxWidth="sm">
        <DialogTitle>Add Tools to Agent: {agent?.name}</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            id="add-tools-to-agent-autocomplete"
            options={availableToolsForDialog}
            // Display tool name and its slug in the dropdown
            getOptionLabel={(option) => `${option.name} (${option.slug})`} 
            value={selectedToolsInDialog}
            onChange={(event, newValue) => {
              setSelectedToolsInDialog(newValue);
            }}
            // Compare options by their unique ID (UUID) since 'value' stores full tool objects
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Select Tools"
                placeholder="Choose tools to add"
                sx={{mt: 1}}
              />
            )}
            noOptionsText={loading ? "Loading available tools..." : (allSystemTools.length === 0 ? "No system tools found." : "No more tools available to add.")}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddToolDialog} disabled={savingTools}>Cancel</Button>
          <Button
            onClick={handleAddSelectedTools}
            variant="contained"
            // Disable if nothing selected, or saving, or any selected tool somehow lacks a slug
            disabled={selectedToolsInDialog.length === 0 || savingTools || selectedToolsInDialog.some(t => !t.slug)}
            startIcon={savingTools ? <CircularProgress size={20}/> : null}
          >
            {savingTools ? "Adding..." : "Add Selected"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AgentToolsTab;